import { router } from '@inertiajs/react';

import { trackUmamiEvent } from '@/lib/analytics';

export interface Document {
  id: number;
  title: string;
  description: string;
  category: string;
  year: number;
  date: string | null;
  fileSize: string | null;
  url: string | null;
}

interface DocumentsTableProps {
  documents: Document[];
}

const CATEGORY_LABELS: Record<string, string> = {
  Atas: 'Atas',
  Editais: 'Editais',
  Regulamentos: 'Regulamentos',
  Contas: 'Contas',
};

export function DocumentsTable({ documents }: DocumentsTableProps) {
  function handleRowClick(doc: Document) {
    if (doc.url) {
      window.open(doc.url, '_blank');
    }
  }

  function handleDelete(e: React.MouseEvent, doc: Document) {
    e.stopPropagation();
    if (!confirm(`Eliminar "${doc.title}"?`)) return;
    trackUmamiEvent('admin_content_delete', { content_type: 'document' });
    router.delete(`/documents/${doc.id}`, { preserveScroll: true });
  }

  if (documents.length === 0) {
    return (
      <div className="text-on-surface-light flex flex-col items-center gap-2 py-12 text-center text-sm">
        <span className="material-symbols-outlined text-on-surface-light/40 text-3xl">
          description
        </span>
        Nenhum documento encontrado.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-left">
        <thead>
          <tr className="border-line border-b">
            {['Título', 'Categoria', 'Ano', 'Tamanho', 'Data'].map((h) => (
              <th
                key={h}
                className="text-on-surface-light/70 px-4 py-3 text-xs font-bold tracking-wider uppercase"
              >
                {h}
              </th>
            ))}
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => (
            <tr
              key={doc.id}
              onClick={() => handleRowClick(doc)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleRowClick(doc);
                }
              }}
              tabIndex={doc.url ? 0 : undefined}
              aria-label={doc.url ? `Abrir ${doc.title}` : undefined}
              className="border-line hover:bg-background focus:bg-background cursor-pointer border-b transition-colors last:border-b-0 focus:outline-none"
            >
              <td className="text-on-surface px-4 py-3.5 text-sm font-semibold">
                <span className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-on-surface-light/50 text-[16px]">
                    picture_as_pdf
                  </span>
                  {doc.title}
                </span>
              </td>
              <td className="text-on-surface-light px-4 py-3.5 text-sm">
                {CATEGORY_LABELS[doc.category] ?? doc.category}
              </td>
              <td className="text-on-surface-light px-4 py-3.5 text-sm">{doc.year}</td>
              <td className="text-on-surface-light px-4 py-3.5 text-xs">{doc.fileSize ?? '—'}</td>
              <td className="text-on-surface-light px-4 py-3.5 text-xs">
                {doc.date ? new Date(doc.date).toLocaleDateString('pt-PT') : '—'}
              </td>
              <td className="px-4 py-3.5">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={(e) => handleDelete(e, doc)}
                    aria-label="Eliminar documento"
                    className="text-on-surface-light hover:bg-error/10 hover:text-error rounded-md p-1.5 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px]">delete</span>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
